// ─────────────────────────────────────────────
//  coverage — "OB must always be covered" rule.
//
//  WC OB runs 24h on a single on-call CRNA (assignment.isOnCall, ahpId). If that
//  CRNA takes over another room (becomes that room's primary ahp), OB is left
//  uncovered. The board flags the OB card ("Not covered", blinking) until an AIC
//  or supervising doctor acknowledges it; the flag auto-clears when she returns
//  (no other room has her as ahp anymore).
//
//  Pure — no React, no Firestore.
// ─────────────────────────────────────────────

import { isSoloCase, rowClaimsAt, hasSeat } from "./timeline";
import { roomForOrRoom } from "./gridTemplate";

// Acknowledge is stored on the on-call assignment as `coverageAckKey` = the key
// of the absence that was acknowledged. If she moves to a DIFFERENT room the key
// changes, so the alert re-fires (a new absence needs a new acknowledgement).
export function coverageStatus(onCallAssignment, allAssignments, room) {
  const asg = onCallAssignment;
  if (!asg || !asg.isOnCall || !asg.ahpId) return { uncovered: false };

  const home = asg.orRoom;
  let reason = null;
  let awayKey = null;
  let awayRoomName = null;

  // 1) Covering another room — the on-call CRNA is the primary ahp elsewhere.
  const away = (allAssignments || []).find(
    (a) => a && a.id !== asg.id && a.ahpId === asg.ahpId && a.orRoom && a.orRoom !== home
  );
  if (away) {
    reason = "covering";
    awayRoomName = away.orRoom;
    awayKey = `room:${away.orRoom}`;
  } else if (room && room.onBreak) {
    // 2) On break — stepped out of OB.
    reason = "break";
    awayKey = "break";
  } else if (room && room.signOutTime) {
    // 3) Signed out / stepped away from OB.
    reason = "away";
    awayKey = "away";
  }

  if (!awayKey) return { uncovered: false };

  return {
    uncovered: true,
    reason, // "covering" | "break" | "away"
    awayRoomName,
    awayKey,
    acked: asg.coverageAckKey === awayKey,
    ackByName: asg.coverageAckByName || null,
  };
}

// ─────────────────────────────────────────────
//  A ROOM RUNNING WITH NOBODY IN IT.
//
//  ⚠️ THE ALARM EXISTED AND HAS NEVER APPLIED TO AN ORDINARY ROOM. The
//  "uncovered" item is severity 1 and NEEDS YOU NOW names "rooms running
//  without a provider" among the things it watches — but `uncoveredDetail`
//  opens with `if (!asg.isOnCall) continue`, so every row it has ever examined
//  is an on-call watch. The OB rule above was wearing a general name.
//
//  Live, 2026-09-13: WOR 12 ran surgeon Mumtaz 07:30 → 11:40 with `ahpId: null`
//  and `ahpName: ""` — the only row of the day with no CRNA — and nothing
//  reported it anywhere. Worse, the bar printed the supervising doctor in the
//  seat, so the room read as staffed.
//
//  ⚠️ AND A GENUINE SOLO DOCTOR ROOM IS NOT THIS. An empty CRNA cell is also
//  what a doctor personally doing a case looks like — the importer writes
//  `ahpName: ""` for both. `isSoloCase` is the one place that separates them,
//  using the day's other rows (a doctor supervising elsewhere at the same hour
//  is not standing in this room), and this asks it rather than re-deciding, so
//  the alarm and the bar label can never disagree about whether the room is
//  occupied.
//
//  ⚠️ AND IT HAS TO BE RUNNING NOW. `rowClaimsAt` is the same predicate the
//  bench, FREE NOW and the break board use. An alarm that outlives its case is
//  how staff learn to scroll past this tier ([[alarm-colour-is-finite]]).
// ─────────────────────────────────────────────
export function unstaffedRooms(assignments, rooms, nowMin = null) {
  if (!Number.isFinite(nowMin)) return [];
  const list = Array.isArray(assignments) ? assignments : [];
  const out = [];
  const seen = new Set();
  for (const asg of list) {
    if (!asg) continue;
    // The OB watch has its own rule above; reporting it here would double-count
    // the one room this module was originally written for.
    if (asg.isOnCall) continue;
    if (!rowClaimsAt(asg, nowMin)) continue;
    // Somebody IS in the room, or the doctor genuinely is: either way it is not
    // empty. `isSoloCase` returns false for a seatless row whose attending is
    // holding another room at the same time — which is exactly the gap.
    if (isSoloCase(asg, list)) continue;
    if (hasSeat(asg)) continue;
    const room = roomForOrRoom(asg.orRoom, rooms);
    if (!room || seen.has(room.id)) continue;
    seen.add(room.id);
    out.push({
      roomId: room.id,
      roomName: room.name || asg.orRoom || "",
      // Nobody to name — that IS the finding. The worklist prints the room.
      providerName: "",
      reason: "unstaffed",
      awayRoomName: "",
    });
  }
  return out;
}
